import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Wifi, WifiOff, RefreshCw } from 'lucide-react'
import ConnectionService from '../services/connectionService'
import toast from 'react-hot-toast'

const ConnectionStatus: React.FC = () => {
  const [status, setStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking')
  const [isRetrying, setIsRetrying] = useState(false)
  const connectionService = ConnectionService.getInstance()

  useEffect(() => {
    checkConnection()
    
    // Vérifier périodiquement la connexion
    const interval = setInterval(checkConnection, 60000) // Vérifier toutes les minutes
    return () => clearInterval(interval)
  }, [])

  const checkConnection = async () => {
    setStatus('checking')
    await connectionService.initialize()
    setStatus(connectionService.getConnectionStatus())
  }

  const handleRetry = async () => {
    setIsRetrying(true)
    const success = await connectionService.retryConnection()
    setStatus(success ? 'connected' : 'disconnected')
    setIsRetrying(false)
    
    if (success) {
      toast.success('Connexion rétablie!')
    } else {
      toast.error('Impossible de se connecter à Supabase')
    }
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          icon: Wifi,
          color: 'text-green-600 bg-green-50 border-green-200',
          text: 'Supabase Connecté',
          description: 'Données en temps réel'
        }
      case 'disconnected':
        return {
          icon: WifiOff,
          color: 'text-orange-600 bg-orange-50 border-orange-200',
          text: 'Mode Démo',
          description: 'Données simulées'
        }
      default:
        return {
          icon: RefreshCw,
          color: 'text-blue-600 bg-blue-50 border-blue-200',
          text: 'Vérification...',
          description: 'Test de connexion'
        }
    }
  }

  const config = getStatusConfig()
  const Icon = config.icon

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center px-3 py-2 rounded-lg border text-sm font-medium ${config.color}`}
    >
      <Icon className={`w-4 h-4 mr-2 ${status === 'checking' ? 'animate-spin' : ''}`} />
      <div className="flex flex-col">
        <span className="font-medium">{config.text}</span>
        <span className="text-xs opacity-75">{config.description}</span>
      </div>
      
      {status === 'disconnected' && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleRetry}
          disabled={isRetrying}
          className="ml-2 p-1 hover:bg-orange-100 rounded transition-colors disabled:opacity-50"
          title="Réessayer la connexion"
        >
          <RefreshCw className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
        </motion.button>
      )}
    </motion.div>
  )
}

export default ConnectionStatus